import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarClock } from 'lucide-react'
import { useFilterValueCounts } from '../useFilterValueCounts'
import { MultiSelectCommandList } from './MultiSelectCommandList'
import type { ValuePickerProps } from './types'
import { DUE_PRESETS } from '@/lib/filters/duePresets'
import { isDueDateOperator } from '@/lib/filters/fieldDefinitions'
import { Calendar } from '@/components/ui/calendar'
import { cn } from '@/lib/utils'

export function DueFilterPicker({ selectedValues, onToggleValue, onSetValues, operator }: ValuePickerProps) {
  const { t } = useTranslation('tasks')
  const counts = useFilterValueCounts('due')
  const options = useMemo(
    () =>
      DUE_PRESETS.map((preset) => ({
        value: preset,
        label: t(`filters.due.${preset}`),
        icon: (
          <CalendarClock
            className={cn('size-3.5', preset === 'overdue' ? 'text-brand' : 'text-muted-foreground')}
          />
        ),
      })),
    [t],
  )

  // before / on / after compare against a day (typed `due:<fri`, design 9b).
  if (operator && isDueDateOperator(operator)) {
    const day = selectedValues[0] === undefined ? undefined : new Date(Number(selectedValues[0]))
    return (
      <div className="bg-popover">
        <p className="px-3 pt-2 text-xs text-muted-foreground">{t('filters.pickDay')}</p>
        <Calendar
          mode="single"
          selected={day}
          defaultMonth={day}
          onSelect={(date) => {
            if (!date) return
            const start = new Date(date)
            start.setHours(0, 0, 0, 0)
            if (onSetValues) onSetValues([start.getTime()])
            else onToggleValue(start.getTime())
          }}
        />
      </div>
    )
  }

  return (
    <MultiSelectCommandList
      options={options}
      selectedValues={selectedValues}
      onToggle={onToggleValue}
      searchPlaceholder={t('filters.menu.searchDates')}
      counts={counts}
    />
  )
}
